import { useEffect, useMemo, useState } from 'react';
import Modal from '../UI/Modals/Modal';
import { Stepper } from '../UI/Stepper/Stepper';
import { useConsultationContext } from '../../context/ConsultationContext';

import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  IAccountInfo,
  selectAccountInfo,
  selectBusinessPartnerId,
  selectDirtyState,
  selectMerchantPersonId,
  selectUserRole,
  setDirtyState,
} from '../../store/slices/auth';
import {
  createWorkOrderService,
  updateStatusOfConsultation,
} from '../../store/slices/workOrderServiceConsultation';
import { toast } from 'react-toastify';
import POSITION = toast.POSITION;
import { NURSE_USER_ROLE } from '../../constants/userRolesConstants';
import { fetchSalesOrders } from '../../store/slices/salesOrders';
import { ConsultationStatusTypes, STEPPER_ACTIVE_STEPS } from '../../enums/enums';
import { ConsultationTypes, DoctorLetterTypeId } from '../../enums/enums';
import { useNavigate } from 'react-router-dom';
import PrimaryButtonV2 from '../UI/Buttons/PrimaryButtonV2';
import BackButtonIcon from '../../assets/icons/back-button-icon.svg';
import downloadIcon from '../../assets/icons/download-icon.svg';
import styles from './Header.module.scss';
import { useTranslation } from '../../hooks/useTranslations';

export default function Header() {
  const { translations } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    selectedSalesOrder,
    workOrderStatusId,
    setWorkOrderStatusId,
    clearTabs,
    closeConsultation,
    productOrder,
    isReports,
  } = useConsultationContext();

  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const userRole: string = useAppSelector<string>(selectUserRole);
  const businessPartnerId = useAppSelector<string>(selectBusinessPartnerId);
  const merchantPersonId = useAppSelector<string>(selectMerchantPersonId);
  const [modal, setModal] = useState<string>('');
  const transparentBtnStyle = {
    minWidth: 160,
    height: 'auto',
    padding: '10px 15px',
    backgroundColor: 'transparent',
    border: '1px solid rgb(11, 27, 92)',
    color: 'rgb(11, 27, 92)',
  };

  useEffect(() => {
    dispatch(fetchSalesOrders());
    return () => clearTabs();
  }, [dispatch]);
  const handleConsultationButton = async () => {
    if (modal === 'label.customerNotJoined') {
      if (!workOrderStatusId) {
        toast.error(translations('label.start_consultation'), { position: POSITION.BOTTOM_RIGHT });
        setModal('');
        return;
      }
      closeConsultation(true);
      return;
    }
    if (modal === 'finalize.consultation') {
      closeConsultation(false);
      return;
    }
    // nurse user can start the onsite video consultations, but it should redirect to PreJoinScreen page
    if (
      userRole === NURSE_USER_ROLE &&
      selectedSalesOrder?.service_service_group?.service_group_id ===
        ConsultationTypes.ON_SITE_VIDEO_CONSULTATION &&
      ((workOrderStatusId === 1 && !accountInfo.consultation_with_extra_steps) ||
        (workOrderStatusId === 5 && accountInfo.consultation_with_extra_steps))
    ) {
      navigate('/web/prejoin-consultation', {
        state: {
          ...selectedSalesOrder,
          person: selectedSalesOrder?.customer_person,
          sales_order: selectedSalesOrder?.sales_order,
          sales_order_item: selectedSalesOrder?.sales_order_item,
          sales_order_appointment: selectedSalesOrder?.sales_order_appointment,
          service: selectedSalesOrder?.service,
          work_order: {
            ...selectedSalesOrder?.work_order,
            work_order_id: workOrderStatusId,
          },
        },
      });
      return;
    }
    if (workOrderStatusId === undefined) {
      await dispatch(
        createWorkOrderService({
          serviceId: selectedSalesOrder?.service.service_id,
          salesOrderItemId: selectedSalesOrder?.sales_order_item?.sales_order_item_id,
          salesOrderId: selectedSalesOrder?.sales_order.sales_order_id,
          personId: selectedSalesOrder?.sales_order.business_person_id,
          businessPartnerId: businessPartnerId,
        })
      );
      const salesOrdersResponse = await dispatch(fetchSalesOrders());
      if (!accountInfo.consultation_with_extra_steps) {
        const salesOrderToSelect = salesOrdersResponse?.payload?.data?.salesOrders
          ? salesOrdersResponse?.payload?.data?.salesOrders?.find(
              (s: any) =>
                s.sales_order_info?.sales_order?.sales_order_id ===
                  selectedSalesOrder?.sales_order.sales_order_id &&
                s.sales_order_info?.sales_order_item?.sales_order_item_id ===
                  selectedSalesOrder?.sales_order_item?.sales_order_item_id
            )
          : null;
        if (salesOrderToSelect) {
          await dispatch(
            updateStatusOfConsultation({
              workOrderId: salesOrderToSelect?.sales_order_info.work_order?.work_order_id ?? 0,
              workOrderStatusId: 6,
              details: {},
              taId: salesOrderToSelect?.sales_order_info.sales_order?.ta_id,
              transactionId: salesOrderToSelect?.sales_order_info?.sales_order?.transaction_id,
              personEmail: salesOrderToSelect?.sales_order_info?.customer_person?.person_email,
              personAccountNumber:
                salesOrderToSelect?.sales_order_info?.customer_person?.person_account_number,
            })
          );
          setWorkOrderStatusId(6);
        }
      }
      if (accountInfo.consultation_with_extra_steps) setWorkOrderStatusId(1);
      return;
    }
    if (accountInfo.consultation_with_extra_steps && workOrderStatusId == 1) {
      dispatch(
        updateStatusOfConsultation({
          workOrderId: selectedSalesOrder?.work_order?.work_order_id ?? 0,
          workOrderStatusId: 5,
          details: {},
          taId: selectedSalesOrder?.sales_order.ta_id,
          transactionId: selectedSalesOrder?.sales_order.transaction_id,
          personEmail: selectedSalesOrder?.customer_person.person_email,
          personAccountNumber: selectedSalesOrder?.customer_person.person_account_number,
        })
      );
      setWorkOrderStatusId(5);
      return;
    }
    if (!accountInfo.consultation_with_extra_steps && workOrderStatusId == 1) {
      const salesOrdersResponse = await dispatch(fetchSalesOrders());

      if (selectedSalesOrder.service_service_group.service_group_id === 11) {
        const salesOrderToSelect = salesOrdersResponse?.payload?.data?.salesOrders
          ? salesOrdersResponse?.payload?.data?.salesOrders?.find(
              (s: any) =>
                s.sales_order_info?.sales_order?.sales_order_id ===
                  selectedSalesOrder?.sales_order.sales_order_id &&
                s.sales_order_info?.sales_order_item?.sales_order_item_id ===
                  selectedSalesOrder?.sales_order_item?.sales_order_item_id
            )
          : null;
        dispatch(
          updateStatusOfConsultation({
            workOrderId: selectedSalesOrder?.work_order?.work_order_id ?? 0,
            workOrderStatusId: 6,
            details: {},
            taId: selectedSalesOrder?.sales_order.ta_id,
            transactionId: selectedSalesOrder?.sales_order.transaction_id,
            personEmail: selectedSalesOrder?.customer_person.person_email,
            personAccountNumber: selectedSalesOrder?.customer_person.person_account_number,
            appointmentStatus: ConsultationStatusTypes.Started,
            salesOrderId: salesOrderToSelect?.sales_order_info?.sales_order?.sales_order_id,
            salesOrderExternalId:
              salesOrderToSelect?.sales_order_info?.sales_order?.sales_order_external_id,
            merchantAccountNumber: accountInfo?.account_number.toString(),
            merchantPersonId: accountInfo?.external_person_id?.toString(),
            externalSalesOrderItemId:
              salesOrderToSelect?.sales_order_info?.sales_order_item?.external_sales_order_item_id,
            personIdCreate: accountInfo?.personIdCreate,
          })
        );
        setWorkOrderStatusId(6);
        return;
      }
      dispatch(
        updateStatusOfConsultation({
          workOrderId: selectedSalesOrder?.work_order?.work_order_id ?? 0,
          workOrderStatusId: 6,
          details: {},
          taId: selectedSalesOrder?.sales_order.ta_id,
          transactionId: selectedSalesOrder?.sales_order.transaction_id,
          personEmail: selectedSalesOrder?.customer_person.person_email,
          personAccountNumber: selectedSalesOrder?.customer_person.person_account_number,
        })
      );
      setWorkOrderStatusId(6);
      return;
    }
    if (workOrderStatusId == 5) {
      dispatch(
        updateStatusOfConsultation({
          workOrderId: selectedSalesOrder?.work_order?.work_order_id ?? 0,
          workOrderStatusId: 6,
          details: {},
          taId: selectedSalesOrder?.sales_order.ta_id,
          transactionId: selectedSalesOrder?.sales_order.transaction_id,
          personEmail: selectedSalesOrder?.customer_person.person_email,
          personAccountNumber: selectedSalesOrder?.customer_person.person_account_number,
        })
      );
      setWorkOrderStatusId(6);
      return;
    }
    if (workOrderStatusId == 6) {
      dispatch(
        updateStatusOfConsultation({
          workOrderId: selectedSalesOrder?.work_order?.work_order_id ?? 0,
          workOrderStatusId: 4,
          details: {},
          taId: selectedSalesOrder?.sales_order.ta_id,
          transactionId: selectedSalesOrder?.sales_order.transaction_id,
          personEmail: selectedSalesOrder?.customer_person.person_email,
          personAccountNumber: selectedSalesOrder?.customer_person.person_account_number,
        })
      );
      setWorkOrderStatusId(4);
      navigate('/web/schedule');
      return;
    }
    if (workOrderStatusId == 4) {
      closeConsultation(false);
    }
    if (workOrderStatusId == 2) {
      navigate('/web/schedule');
      return;
    }
    // if (userRole === NURSE_USER_ROLE) {
    //   if (selectedSalesOrder?.service_service_group.service_group_id === ConsultationTypes.ON_SITE_VIDEO_CONSULTATION)
    //     navigate('/web/prejoin-consultation', {
    //       state: {
    //         ...selectedSalesOrder,
    //       },
    //     });
    //   return;
    // }
    // if (selectedSalesOrder?.service_service_group.service_group_id === ConsultationTypes.FACE_TO_FACE_CONSULTATION) {
    //   navigate('/web/facetoface-consulting-room', {
    //     state: {
    //       ...selectedSalesOrder,
    //     },
    //   });
    // } else {
    //   navigate('/web/video-consulting-room', {
    //     state: {
    //       ...selectedSalesOrder,
    //     },
    //   });
    // }
  };

  const handleConsultationButtonLabel = () => {
    if (
      userRole === NURSE_USER_ROLE &&
      selectedSalesOrder?.service?.service_id === ConsultationTypes.IN_CLINIK_CONSULTATION &&
      (workOrderStatusId === 5 || workOrderStatusId === 6)
    ) {
      return translations('button.join_video_call');
    }
    if (workOrderStatusId === 1) {
      return accountInfo.consultation_with_extra_steps
        ? translations('consultation.prepared')
        : selectedSalesOrder.service_service_group.service_group_id ===
            ConsultationTypes.FACE_TO_FACE_CONSULTATION ||
          selectedSalesOrder.service_service_group.service_group_id ===
            ConsultationTypes.REVIEW_CONSULTATION
        ? translations('start.consultation')
        : translations('start.video.consultation');
    }
    if (workOrderStatusId === 4) {
      return translations('finalize.consultation');
    }
    if (workOrderStatusId === 6) {
      return translations('park.consultation');
    }
    if (workOrderStatusId === 5) {
      if (
        selectedSalesOrder.service_service_group.service_group_id ===
          ConsultationTypes.FACE_TO_FACE_CONSULTATION ||
        selectedSalesOrder.service_service_group.service_group_id ===
          ConsultationTypes.REVIEW_CONSULTATION
      ) {
        return translations('start.consultation');
      } else {
        return translations('start.video.consultation');
      }
    }
    return accountInfo.consultation_with_extra_steps
      ? translations('prepare.consultation')
      : selectedSalesOrder.service_service_group.service_group_id ===
          ConsultationTypes.FACE_TO_FACE_CONSULTATION ||
        selectedSalesOrder.service_service_group.service_group_id ===
          ConsultationTypes.REVIEW_CONSULTATION
      ? translations('start.consultation')
      : translations('start.video.consultation');
    // return translations('prepare.consultation')
    // if (accountInfo.consultation_with_multiple_steps) {
    //   return translations('prepare.consultation')
    // }
  };

  // nurse user can only start the onsite video consultations, but it will open PreJoinScreen page
  const isConsultationButtonDisabled = useMemo(() => {
    if (
      (handleConsultationButtonLabel() === translations('start.consultation') ||
        handleConsultationButtonLabel() === translations('start.video.consultation')) &&
      selectedSalesOrder?.service_service_group?.service_group_id !==
        ConsultationTypes.ON_SITE_VIDEO_CONSULTATION &&
      selectedSalesOrder?.service_service_group?.service_group_id !==
        ConsultationTypes.FACE_TO_FACE_CONSULTATION &&
      selectedSalesOrder?.service_service_group?.service_group_id !==
        ConsultationTypes.REVIEW_CONSULTATION &&
      userRole === NURSE_USER_ROLE
    ) {
      if (accountInfo.consultation_with_extra_steps && workOrderStatusId === 5) {
        return true;
      }
      if (
        !accountInfo.consultation_with_extra_steps &&
        (workOrderStatusId === 1 || workOrderStatusId === undefined)
      ) {
        return true;
      }
    }
    return false;
  }, [accountInfo, userRole, translations, selectedSalesOrder, workOrderStatusId]);

  const isDirtyState = useAppSelector(selectDirtyState);

  const handleBackButton = () => {
    if (!isDirtyState.isDirty) {
      // navigate('/web/reports?tabIdx=1', { replace: true });
      navigate(-1);
    } else {
      dispatch(
        setDirtyState({
          isDirty: true,
          isModalOpen: true,
          component: isDirtyState.component,
        })
      );
    }
  };

  const handleDownload = () => {
    const doctorAttachmentFound: any = productOrder?.attachments?.find((att: any) =>
      DoctorLetterTypeId.includes(att.attachment_type_id)
    );

    if (doctorAttachmentFound && doctorAttachmentFound.link) {
      window.open(
        `${window.location.protocol}//${window.location.host}${doctorAttachmentFound?.link}`
      );
    } else {
      toast.error(translations('error.no_attachments').toString(), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  return (
    <>
      {isReports && (
        <div className={styles.mainHeader}>
          <p className={styles.title}>{`${translations('customer.report')} / ${
            selectedSalesOrder?.customer_person?.person_firstname
          } ${selectedSalesOrder?.customer_person?.person_lastname}`}</p>
          <img
            style={{ cursor: 'pointer', width: '110px' }}
            src={BackButtonIcon}
            alt="BackButton"
            onClick={handleBackButton}
          />
        </div>
      )}
      <div className={styles.header}>
        {accountInfo.consultation_with_extra_steps ? (
          <Stepper
            steps={[
              { displayText: translations('open') },
              { displayText: translations('prepare') },
              { displayText: translations('prepared') },
              { displayText: translations('started') },
              { displayText: translations('parked') },
              { displayText: translations('finalized') },
            ]}
            activeStep={STEPPER_ACTIVE_STEPS[workOrderStatusId] || 0}
          />
        ) : (
          <div />
        )}
        {/* IF IS NOT FINALIZED SHOW BUTTONS */}
        <div className={styles.actions}>
          {isReports ? (
            workOrderStatusId === 2 ? (
              productOrder?.attachments?.length > 0 && (
                <div className={styles.buttonsWrapper}>
                  <div
                    className={styles.doctorLetterDownload}
                    onClick={handleDownload}
                    style={{ cursor: 'pointer' }}
                  >
                    <span>{translations('consultation.letter')}</span>
                    <img className={styles.downloadIcon} src={downloadIcon} alt="download" />
                  </div>
                </div>
              )
            ) : (
              <>
                <div className={styles.buttonsWrapper}>
                  <p>{translations('action')}:</p>
                  <PrimaryButtonV2
                    text={translations('finalize.consultation')}
                    onClick={() => closeConsultation(false)}
                    buttonStyle={{ minWidth: 170 }}
                  />
                </div>
              </>
            )
          ) : (
            workOrderStatusId !== 2 && (
              <>
                <div className={styles.buttonsWrapper}>
                  <p>{translations('mark')}:</p>
                  <PrimaryButtonV2
                    text={translations('label.customerNotJoined')}
                    onClick={() => setModal('label.customerNotJoined')}
                    buttonStyle={transparentBtnStyle}
                  />
                </div>
                <div className={styles.buttonsWrapper}>
                  <p>{translations('action')}:</p>
                  <PrimaryButtonV2
                    text={handleConsultationButtonLabel()}
                    onClick={() =>
                      workOrderStatusId === 6
                        ? setModal('park.consultation')
                        : handleConsultationButton()
                    }
                    buttonStyle={workOrderStatusId === 6 ? transparentBtnStyle : {}}
                    disabled={isConsultationButtonDisabled}
                  />
                  {workOrderStatusId === 6 && (
                    <PrimaryButtonV2
                      text={translations('finalize.consultation')}
                      onClick={() => setModal('finalize.consultation')}
                      buttonStyle={{ minWidth: 170 }}
                    />
                  )}
                </div>
              </>
            )
          )}
        </div>
      </div>
      <Modal isOpen={modal !== ''} onClose={() => setModal('')} containerPadding={'20px 10px'}>
        <div className={styles.modalHeader}>
          <p className={styles.title}>{translations(modal)}</p>
          <p className={styles.label}>
            {modal === 'label.customerNotJoined'
              ? translations('label.proceed_with_action')
              : translations('label.proceed_with_actions_v2')}
          </p>
        </div>
        <div className={styles.modalButtons}>
          <PrimaryButtonV2
            text={translations('yes')}
            containerStyle={{ marginRight: 20 }}
            buttonStyle={{ height: 50, width: 170 }}
            onClick={() => handleConsultationButton()}
          />
          <PrimaryButtonV2
            text={translations('no')}
            invertedColors
            buttonStyle={{ height: 50, width: 170 }}
            onClick={() => setModal('')}
          />
        </div>
      </Modal>
    </>
  );
}
